/**
 * Teclado en navegadores móviles (Chrome Android, Safari iOS).
 *
 * Por defecto el teclado solo encoge el «visual viewport»: los elementos `position: fixed`
 * (el asistente) se quedan detrás del teclado. index.html pide `interactive-widget=resizes-content`
 * (Chrome encoge la página y no hace falta nada más); para el resto calculamos cuánto tapa el teclado
 * y lo dejamos en la variable CSS --kb-inset, que usa .assistant-dock.
 */
export function installKeyboardInset(): () => void {
  if (typeof window === "undefined") return () => undefined;
  const vv = window.visualViewport;
  if (!vv) return () => undefined;
  const root = document.documentElement;
  let frame = 0;
  let wasOpen = false;
  const update = () => {
    frame = 0;
    const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    // Menos de 80 px suele ser la barra del navegador, no el teclado.
    const open = inset > 80;
    root.style.setProperty("--kb-inset", `${open ? inset : 0}px`);
    root.style.setProperty("--vv-height", `${Math.round(vv.height)}px`);
    root.classList.toggle("kb-open", open);
    // Solo al abrirse el teclado: asegura que el campo enfocado queda visible (sin pelear con el scroll del usuario).
    if (open && !wasOpen) {
      const active = document.activeElement as HTMLElement | null;
      if (active && /^(INPUT|TEXTAREA)$/.test(active.tagName) && !active.closest(".assistant-dock")) {
        active.scrollIntoView({ block: "nearest" });
      }
    }
    wasOpen = open;
  };
  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };
  vv.addEventListener("resize", schedule);
  vv.addEventListener("scroll", schedule);
  update();
  return () => {
    vv.removeEventListener("resize", schedule);
    vv.removeEventListener("scroll", schedule);
    if (frame) window.cancelAnimationFrame(frame);
  };
}
